import { supabase } from "@/integrations/supabase/client";
import type { SelectedItem } from "@/lib/selected-weeks";
import { watermarkImage } from "@/lib/watermark";
import { toWebP, safeContentType } from "@/lib/image-convert";
import {
  SETTING_WATERMARK_URL,
  SETTING_WATERMARK_X,
  SETTING_WATERMARK_Y,
  SETTING_WATERMARK_SCALE,
  SETTING_WATERMARK_OPACITY,
  getSettingInt,
  getSettingText,
} from "@/lib/app-settings";

export type WeekPickRow = {
  id: string;
  year: number;
  week: number;
  photographer_id: string;
  image_url: string;
  caption: string | null;
  position: number;
  source_url: string | null;
};

export async function fetchPicksForWeek(year: number, week: number): Promise<SelectedItem[]> {
  const { data: picks, error } = await supabase
    .from("selected_week_picks")
    .select("id, photographer_id, image_url, caption, position, created_at")
    .eq("year", year)
    .eq("week", week)
    .order("position", { ascending: true })
    .order("created_at", { ascending: false });
  if (error || !picks?.length) return [];

  const ids = Array.from(new Set(picks.map((p) => p.photographer_id)));
  const { data: people } = await supabase
    .from("photographers")
    .select("id, slug, name, city, region, instagram_handle, website_url")
    .in("id", ids);
  const byId = new Map((people ?? []).map((p) => [p.id, p]));

  return picks.map((p) => {
    const person = byId.get(p.photographer_id);
    return {
      id: p.id,
      image: p.image_url,
      caption: p.caption ?? person?.city ?? person?.region ?? person?.name ?? "",
      photographerId: person?.slug ?? "",
      photographerName: person?.name ?? "",
      region: person?.city ?? person?.region ?? null,
      instagramHandle: person?.instagram_handle ?? null,
      websiteUrl: person?.website_url ?? null,
    } satisfies SelectedItem;
  });
}

export async function fetchPickedWeeks(): Promise<{ year: number; week: number }[]> {
  const { data, error } = await supabase
    .from("selected_week_picks")
    .select("year, week")
    .order("year", { ascending: false })
    .order("week", { ascending: false });
  if (error || !data) return [];
  const seen = new Set<string>();
  const out: { year: number; week: number }[] = [];
  for (const r of data) {
    const k = `${r.year}-${r.week}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ year: r.year, week: r.week });
  }
  return out;
}

export async function fetchAdminPicksForWeek(year: number, week: number): Promise<WeekPickRow[]> {
  const { data, error } = await supabase
    .from("selected_week_picks")
    .select("id, year, week, photographer_id, image_url, caption, position, source_url")
    .eq("year", year)
    .eq("week", week)
    .order("position", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as WeekPickRow[];
}

export async function fetchAllPickedSources(): Promise<Map<string, { year: number; week: number }>> {
  const { data, error } = await supabase
    .from("selected_week_picks")
    .select("source_url, image_url, year, week");
  if (error) throw new Error(error.message);
  const map = new Map<string, { year: number; week: number }>();
  for (const row of data ?? []) {
    const slot = { year: row.year, week: row.week };
    if (row.source_url) map.set(row.source_url, slot);
    if (row.image_url) map.set(row.image_url, slot);
  }
  return map;
}

export async function addPick(input: {
  year: number;
  week: number;
  photographerDbId: string;
  image_url: string;
  caption?: string | null;
  position?: number;
}): Promise<void> {
  const watermarkedUrl = await applyWatermarkAndUpload(input.image_url);
  const { error } = await supabase.from("selected_week_picks").insert({
    year: input.year,
    week: input.week,
    photographer_id: input.photographerDbId,
    image_url: watermarkedUrl,
    caption: input.caption ?? null,
    position: input.position ?? 0,
    source_url: input.image_url,
  });
  if (error) throw new Error(error.message);
}

export async function removePick(id: string): Promise<void> {
  const { error } = await supabase.from("selected_week_picks").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

export async function removePicks(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const { error } = await supabase.from("selected_week_picks").delete().in("id", ids);
  if (error) throw new Error(error.message);
}

export async function updatePickPositions(items: { id: string; position: number }[]): Promise<void> {
  await Promise.all(
    items.map((it) =>
      supabase.from("selected_week_picks").update({ position: it.position }).eq("id", it.id),
    ),
  );
}

async function applyWatermarkAndUpload(srcUrl: string): Promise<string> {
  try {
    const res = await fetch(srcUrl, { mode: "cors" });
    if (!res.ok) throw new Error(`fetch ${res.status}`);
    const blob = await res.blob();
    const type = blob.type || "image/jpeg";
    const ext = type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";
    const file = new File([blob], `pick.${ext}`, { type });
    const [imageUrl, x, y, scale, opacity] = await Promise.all([
      getSettingText(SETTING_WATERMARK_URL, null),
      getSettingInt(SETTING_WATERMARK_X, 50),
      getSettingInt(SETTING_WATERMARK_Y, 92),
      getSettingInt(SETTING_WATERMARK_SCALE, 25),
      getSettingInt(SETTING_WATERMARK_OPACITY, 94),
    ]);
    const watermarked = await watermarkImage(file, { imageUrl, x, y, scale, opacity });
    const finalFile = await toWebP(watermarked).catch(() => watermarked);
    const outExt = (finalFile.name.split(".").pop() || ext).toLowerCase();
    const path = `_selected/${Date.now()}-${crypto.randomUUID()}.${outExt}`;
    const { error: uploadErr } = await supabase.storage
      .from("community-selected")
      .upload(path, finalFile, { cacheControl: "31536000", contentType: safeContentType(finalFile), upsert: false });
    if (uploadErr) throw new Error(uploadErr.message);
    const { data: pub } = supabase.storage.from("community-selected").getPublicUrl(path);
    return pub.publicUrl;
  } catch (e) {
    console.warn("[selected-pick] watermark fallback to original", e);
    return srcUrl;
  }
}